/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { QueryDslQueryContainer } from '@elastic/elasticsearch/lib/api/types';
import type { ElasticsearchQuery } from 'searchkit';

import type {
  MetaParagraphsContentTypes,
  ParagraphContentOverview,
  ParagraphHighlightedSelection,
} from '../../../types/uncinc_profile/paragraphs';
import type { SearchAllContentHit } from '../../SearchAllContent/types';

function constructAdvancedFilters(
  queryFilters: QueryDslQueryContainer,
  paragraph: ParagraphHighlightedSelection | ParagraphContentOverview,
): QueryDslQueryContainer {
  const filters = queryFilters;
  const contentType = paragraph.field_content_type[0].value;

  if (contentType === 'article') {
    paragraph.field_article_types.forEach((articleType) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          article_type_tid: {
            value: articleType.target_id,
          },
        },
      });
    });
  }

  if (contentType === 'project') {
    paragraph.field_project_types.forEach((projectType) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          project_types_tid: {
            value: projectType.target_id,
          },
        },
      });
    });
    paragraph.field_project_statuses.forEach((projectStatus) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          project_status_tid: {
            value: projectStatus.target_id,
          },
        },
      });
    });
  }

  // Any Content Type can have labels
  paragraph.field_labels.forEach((label) => {
    // @ts-ignore
    filters.bool?.should?.push({
      term: {
        labels_tid: {
          value: label.target_id,
        },
      },
    });
  });

  return filters;
}

export function constructAdditionalSelectionFilters(
  highlightedSelection: ParagraphHighlightedSelection,
): QueryDslQueryContainer {
  const contentType = highlightedSelection.field_content_type[0].value;
  const mode = highlightedSelection.field_selection_mode_highlighted[0].value;

  let filters: QueryDslQueryContainer = {
    bool: {
      should: [],
    },
  };

  switch (mode) {
    case 'highlighted':
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          promoted_to_frontpage: {
            value: true,
          },
        },
      });
      break;

    case 'advanced':
      filters = constructAdvancedFilters(
        filters,
        highlightedSelection,
      );
      break;

    case 'manual':
      if (contentType === 'article') {
        highlightedSelection.field_selected_articles.forEach((article) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: article.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'project') {
        highlightedSelection.field_selected_projects.forEach((project) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: project.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'story') {
        highlightedSelection.field_selected_stories.forEach((story) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: story.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'vacancy') {
        highlightedSelection.field_selected_vacancies.forEach((vacancy) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: vacancy.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'profile') {
        highlightedSelection.field_selected_profiles.forEach((profile) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              profile_id: {
                value: profile.target_id,
              },
            },
          });
        });
      }
      break;

    default:
      break;
  }

  return filters;
}

export function constructAdditionalOverviewFilters(
  contentOverview: ParagraphContentOverview,
): QueryDslQueryContainer {
  const mode = contentOverview.field_selection_mode_overview[0].value;

  let filters: QueryDslQueryContainer = {
    bool: {
      should: [],
    },
  };

  switch (mode) {
    case 'advanced':
      filters = constructAdvancedFilters(
        filters,
        contentOverview,
      );
      break;

    case 'default':
      break;

    default:
      break;
  }

  return filters;
}

export function constructQuery(
  contentType: MetaParagraphsContentTypes,
): ElasticsearchQuery {
  const contentSearch = {
    content_type: {
      value: contentType,
    },
  };

  // For now, we query hardcoded for the profile type 'default_profile'.
  // In the future we could expand on this.
  const profileSearch = {
    profile_type: {
      value: 'default_profile',
    },
  };

  const searchKey = contentType === 'profile' ? profileSearch : contentSearch;

  return (
    {
      bool: {
        must: {
          term: searchKey,
        },
      },
    }
  );
}

export function constructSortingList(
  highlightedSelection: ParagraphHighlightedSelection,
) {
  const contentType = highlightedSelection.field_content_type[0].value;

  let sortList;

  switch (contentType) {
    case 'article':
      sortList = highlightedSelection.field_selected_articles.map((article) => article.target_id);
      break;
    case 'story':
      sortList = highlightedSelection.field_selected_stories.map((story) => story.target_id);
      break;
    case 'project':
      sortList = highlightedSelection.field_selected_projects.map((article) => article.target_id);
      break;
    case 'vacancy':
      sortList = highlightedSelection.field_selected_vacancies.map((article) => article.target_id);
      break;
    case 'profile':
      sortList = highlightedSelection.field_selected_profiles.map((article) => article.target_id);
      break;
    default:
      break;
  }

  return sortList;
}

export function sortByCustomOrder(
  hits: SearchAllContentHit[],
  sortingList: number[],
) {
  const key = hits[0]?.entity_type[0] === 'profile' ? 'profile_id' : 'nid';

  const result = hits.sort((a, b) => {
    const indexA = sortingList.indexOf(a[key][0]);
    const indexB = sortingList.indexOf(b[key][0]);

    if (indexA < indexB) return -1;
    if (indexA > indexB) return 1;
    return 0;
  });

  return result;
}
